import useStore from 'bz-stores/hook/mobx-inject/useStore';
import type AuthStore from 'bz-stores/lib/auth-store';
import type { PropsWithChildren } from 'react';
import { useContext, useEffect } from 'react';
import React, { createContext } from 'react';
import { useJune } from './hook';
import { observer } from 'mobx-react';
import { useLocation } from 'react-router-dom';
import type { Analytics } from '@june-so/analytics-next';
import { cache } from 'tm3-core/cache';

export const isNonProd = !window.location.host.match(
  /(.*).(tm3app.com|click-hub.com|e-clinic.co.uk|gds.co.uk|privatepracticemanagement.co.uk|caseflow.(ie|co.uk))/gm
);

export const isAlsoNonProd = !!window.location.host.match(
  /(.*)(.(uat|demo|pre-prod|preprod|qa|beta).)(.*)/gm
);

export const JuneContext = createContext<Analytics | undefined>(undefined);

export const JuneProvider = observer(({ children }: PropsWithChildren) => {
  if (isNonProd || isAlsoNonProd) return children;

  let key = 'aUzGSRNirZfIa9zg'; // TM3
  if (cache.app.Click) key = 'RTVart0qedMZjyKH';
  else if (cache.app.GDS) key = 'mKcCqQ3EVH9W2uHG';
  else if (cache.app.EClinic) key = '6wlOGZzL93xhoi8q';
  else if (cache.app.Caseflow) key = 'b77Uw7qWwbMbTfXB';
  else if (cache.app.PPM) key = 'usYIqQ3Yju8PwBis';
  const analytics = useJune(key);

  return <JuneContext.Provider value={analytics}>{children}</JuneContext.Provider>;
});

export const RouteTracking = observer(({ children }: PropsWithChildren) => {
  const analytics = useContext(JuneContext);
  const authStore = useStore<typeof AuthStore>('authStore');
  const license = useStore<typeof LicenseObservable>('license');

  useEffect(() => {
    if (analytics && authStore.user && authStore.user.isAuthenticated) {
      const { email, displayName, clientId } = authStore.user;
      analytics.identify(email, {
        email,
        name: displayName,
        prefix: cache.app.prefix,
        clientId
      });
      analytics.group(clientId.toString(), {
        userId: email,
        prefix: cache.app.prefix,
        name: `${clientId ?? ''}  - ${authStore.user.location.name ?? ''}`,
        isTM3Connect: license?.get('isTM3Connect'),
        isClick: license?.get('isClick'),
        isClickPlus: license?.get('isClickPlus'),
        isEvaluating: license?.get('isEvaluating'),
        practitioners: license?.get('practitioners')
      });
    }
  }, [analytics, license]);

  let location = useLocation();

  useEffect(() => {
    analytics?.page(location.pathname);
  }, [location, analytics]);

  return null;
});
